/**
 * Loads the Stamped reviews widget only when the reviews section is about to scroll into
 * view. Stamped's script, stylesheet, fonts and review API calls add up to a few hundred
 * kilobytes, and most visitors never scroll this far, so nothing is fetched until then.
 */
const STAMPED_SRC = 'https://cdn1.stamped.io/files/widget.min.js';

/** @type {Promise<void> | null} */
let stampedScript = null;

function loadStampedScript() {
  if (window.StampedFn) return Promise.resolve();
  if (stampedScript) return stampedScript;

  stampedScript = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STAMPED_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = STAMPED_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });

  return stampedScript;
}

class FaPdpReviews extends HTMLElement {
  connectedCallback() {
    this.widget = this.querySelector('#stamped-main-widget');
    if (!this.widget) return;

    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer?.disconnect();
          this.#load();
        }
      },
      { rootMargin: `${this.dataset.rootMargin || 600}px 0px` }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async #load() {
    if (this.loaded) return;
    this.loaded = true;
    this.setAttribute('data-state', 'loading');

    try {
      await loadStampedScript();
      // Stamped scans the page for its widget containers when init runs. Skip it if another
      // part of the page has already initialised Stamped and filled this widget.
      if (!this.widget.querySelector('.stamped-container')) {
        window.StampedFn?.init({ apiKey: this.dataset.apiKey, sId: this.dataset.storeId });
      }
      // Keep the reserved height until Stamped has drawn the widget (it renders after init
      // returns); dropping it straight away collapsed the section and pulled the page up.
      await this.#widgetDrawn();
      this.setAttribute('data-state', 'loaded');
    } catch (error) {
      this.setAttribute('data-state', 'error');
    }
  }

  /** Resolves once Stamped has put its widget into the container, or after 8 seconds. */
  #widgetDrawn() {
    const drawn = () => !!this.widget.querySelector('.stamped-container, .stamped-summary, .stamped-reviews');
    if (drawn()) return Promise.resolve();
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (!drawn()) return;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      });
      const timer = setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 8000);
      observer.observe(this.widget, { childList: true, subtree: true });
    });
  }
}

if (!customElements.get('fa-pdp-reviews')) {
  customElements.define('fa-pdp-reviews', FaPdpReviews);
}
